import { RULE_LIMITS, type RoomRules } from '@uno/shared';
import { useT } from '../hooks';
import type { Translation } from '../i18n';

interface Props {
  rules: RoomRules;
  onChange: (patch: Partial<RoomRules>) => void;
  /** Non-hosts see the settings but cannot change them. */
  disabled?: boolean;
}

/** Toggle rows, paired with the catalogue keys that label them. */
const TOGGLES: { key: keyof RoomRules; label: keyof Translation['rules']; hint: keyof Translation['rules'] }[] = [
  { key: 'stackDrawTwo', label: 'stackDrawTwo', hint: 'stackDrawTwoHint' },
  { key: 'stackDrawFour', label: 'stackDrawFour', hint: 'stackDrawFourHint' },
  { key: 'challengeWildFour', label: 'challengeWildFour', hint: 'challengeWildFourHint' },
  { key: 'enforceWildFourLegality', label: 'strictWildFour', hint: 'strictWildFourHint' },
  { key: 'playDrawnCard', label: 'playDrawn', hint: 'playDrawnHint' },
  { key: 'drawUntilPlayable', label: 'drawUntilPlayable', hint: 'drawUntilPlayableHint' },
];

export function RulesEditor({ rules, onChange, disabled = false }: Props) {
  const t = useT();

  return (
    <div className="rules">
      <label className="field">
        <span className="field__label">
          {t.rules.turnTimer} <strong>{rules.turnSeconds}s</strong>
        </span>
        <input
          type="range"
          min={RULE_LIMITS.turnSeconds.min}
          max={RULE_LIMITS.turnSeconds.max}
          step={5}
          value={rules.turnSeconds}
          disabled={disabled}
          onChange={(e) => onChange({ turnSeconds: Number(e.target.value) })}
        />
        <span className="field__hint">{t.rules.turnTimerHint}</span>
      </label>

      <div className="field-row">
        <label className="field">
          <span className="field__label">
            {t.rules.startingHand} <strong>{rules.startingHand}</strong>
          </span>
          <input
            type="range"
            min={RULE_LIMITS.startingHand.min}
            max={RULE_LIMITS.startingHand.max}
            value={rules.startingHand}
            disabled={disabled}
            onChange={(e) => onChange({ startingHand: Number(e.target.value) })}
          />
        </label>

        <label className="field">
          <span className="field__label">
            {t.rules.unoPenalty} <strong>{rules.unoPenalty}</strong>
          </span>
          <input
            type="range"
            min={RULE_LIMITS.unoPenalty.min}
            max={RULE_LIMITS.unoPenalty.max}
            value={rules.unoPenalty}
            disabled={disabled}
            onChange={(e) => onChange({ unoPenalty: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="rules__toggles">
        {TOGGLES.map((toggle) => (
          <label key={toggle.key} className="toggle toggle--card" title={t.rules[toggle.hint]}>
            <input
              type="checkbox"
              checked={Boolean(rules[toggle.key])}
              disabled={disabled}
              onChange={(e) => onChange({ [toggle.key]: e.target.checked } as Partial<RoomRules>)}
            />
            <span>
              <strong>{t.rules[toggle.label]}</strong>
              <em>{t.rules[toggle.hint]}</em>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
